import { color, HStack, isDownArrow, isUpArrow, Text } from '@starrez/starryui9';
import { ChevronDownSmall } from '@starryui10/images/icons/ChevronDownSmall';
import { GetCaptionFromViewType } from '../../../utils/stringUtil';
import React, { createRef, useEffect } from 'react';
import styled from 'styled-components';
import { strings } from '../../../localization';
import { useViewPicker } from '../context/ViewPickerContext';
import { ViewItemIcon } from './ViewItemIcon';
import { selectors } from './ViewItemList';
import { ViewTypeIcon } from './ViewTypeIcon';

export const ViewPickerButton = () => {
    const { isPickerOpen, activeView, updateFavouriteViews, openPicker, closePicker } = useViewPicker();
    const buttonRef = createRef<HTMLButtonElement>();

    useEffect(() => {
        const button = buttonRef.current;
        if (!isPickerOpen && button) {
            button.focus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPickerOpen]);

    const togglePicker = () => {
        isPickerOpen ? closePicker() : openPicker();
    };

    const handleOnKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
        if (isDownArrow(e)) {
            e.stopPropagation();
            isPickerOpen
                ? (buttonRef?.current?.parentElement?.parentElement?.nextSibling as HTMLElement)
                      ?.querySelectorAll<HTMLLIElement>(selectors)[0]
                      .focus()
                : openPicker();
        } else if (isUpArrow(e)) {
            e.stopPropagation();
            isPickerOpen ? closePicker() : openPicker();
        }
    };

    const captionWithViewType = activeView?.caption && `${GetCaptionFromViewType(activeView?.viewType)} ${activeView?.caption}`;
    return (
        <HStack inline gap="xxSmall">
            <Button
                type="button"
                ref={buttonRef}
                aria-label={strings.formatString(strings.ViewPicker_Label_ActiveView, captionWithViewType || '') as string}
                onClick={() => togglePicker()}
                aria-haspopup="listbox"
                onKeyDown={(e) => handleOnKeyDown(e)}
                aria-expanded={isPickerOpen ? true : false}
            >
                <ViewTypeIcon viewType={activeView?.viewType} />
                <Text variant="xLarge.bold">{activeView?.caption}</Text>
                <ChevronWrapper>
                    <ChevronDownSmall />
                </ChevronWrapper>
            </Button>
            {Number(activeView?.id) > 0 && (
                <ViewItemIcon
                    ariaLabel={
                        activeView?.favourite
                            ? (strings.formatString(strings.View_Label_ToggleFavouriteView, captionWithViewType || '') as string)
                            : (strings.formatString(strings.View_Label_ToggleUnfavouriteView, captionWithViewType || '') as string)
                    }
                    color={color.Grey.G70}
                    icon={activeView?.favourite ? 'StarFilled' : 'StarOutline'}
                    onClick={() => activeView && updateFavouriteViews(activeView)}
                    title={activeView?.favourite ? strings.View_Label_ViewNotFavourited : strings.View_Label_ViewFavourited}
                />
            )}
        </HStack>
    );
};

const ChevronWrapper = styled.span`
    margin-left: 8px;
    display: inline-block;
`;

const Button = styled.button`
    font: inherit;
    display: grid;
    gap: 8px;
    grid-auto-flow: column;
    align-items: center;
    height: 46px;
    background: transparent;
    border: 2px solid transparent;
    padding: 8px;
    border-radius: 4px;
    color: ${color.Grey.G80};
    cursor: pointer;
    outline: 0;

    svg {
        color: ${color.Grey.G80};
    }

    &:hover {
        background: ${color.Blue.B20};
        color: ${color.Blue.B60};

        span {
            cursor: pointer;
        }

        svg {
            color: ${color.Blue.B60};
        }
    }

    &:focus {
        box-shadow: 0 0 0 2px ${color.Blue.B80};
        border-radius: 4px;
    }
`;
